import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Modal, Skeleton, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { getTimezoneByRegion } from '../utils/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const { Text, Title, Paragraph } = Typography;

interface CompletedAttempt {
  attempt_id: number;
  test_id: number;
  test_title: string;
  completed_at: string | null;
  score: number | null;
  is_passed: boolean | null;
  has_assignment?: boolean;
  is_counted?: boolean;
}

interface CompletionDetails {
  user_id: number;
  region?: string;
  tests: CompletedAttempt[];
}

interface HistoricalTest {
  test_id: number;
  latest: CompletedAttempt;
  attempts: number;
}

const newestFirst = (a: CompletedAttempt, b: CompletedAttempt) =>
  (b.completed_at || '').localeCompare(a.completed_at || '') || b.attempt_id - a.attempt_id;

const groupTests = (attempts: CompletedAttempt[]): HistoricalTest[] => {
  const grouped = new Map<number, HistoricalTest>();
  attempts.forEach((attempt) => {
    const group = grouped.get(attempt.test_id);
    if (group) group.attempts += 1;
    else grouped.set(attempt.test_id, { test_id: attempt.test_id, latest: attempt, attempts: 1 });
  });
  return Array.from(grouped.values());
};

const result = (attempt: CompletedAttempt) => (
  <Space size={4} wrap>
    <Text strong>{typeof attempt.score === 'number' && Number.isFinite(attempt.score)
      ? `${Number(attempt.score.toFixed(2))}%` : '—'}</Text>
    <Tag color={attempt.is_passed === true ? 'green' : attempt.is_passed === false ? 'red' : undefined}>
      {attempt.is_passed === true ? 'Сдано' : attempt.is_passed === false ? 'Не сдано' : 'Результат неизвестен'}
    </Tag>
  </Space>
);

/** History uses the same read-only source as statistics, including older unassigned attempts.
 * It never creates assignments or uses a historical result to complete a new assignment.
 */
const EmployeeCompletionHistory: React.FC<{ userId: number; revision: number }> = ({ userId, revision }) => {
  const [state, setState] = useState<{ userId: number; status: 'loading' | 'ready' | 'error';
    data?: CompletionDetails; error?: string } | null>(null);
  const [retry, setRetry] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let current = true;
    setOpen(false);
    setState({ userId, status: 'loading' });
    api.get<CompletionDetails>(`/admin/statistics/user/${userId}/details`)
      .then(({ data }) => {
        if (!current) return;
        if (data?.user_id !== userId || !Array.isArray(data.tests)) {
          throw new Error('Некорректные данные истории прохождений');
        }
        setState({ userId, status: 'ready', data });
      })
      .catch((error) => {
        if (current) setState({ userId, status: 'error', error: getApiErrorMessage(error,
          'Не удалось загрузить историю прохождений') });
      });
    return () => { current = false; };
  }, [userId, revision, retry]);

  const data = state?.userId === userId ? state.data : undefined;
  const attempts = useMemo(() => {
    const unique = new Map<number, CompletedAttempt>();
    (data?.tests || []).forEach((attempt) => {
      if (attempt && Number.isSafeInteger(attempt.attempt_id) && attempt.attempt_id > 0
        && Number.isSafeInteger(attempt.test_id) && attempt.test_id > 0) {
        unique.set(attempt.attempt_id, attempt);
      }
    });
    return Array.from(unique.values()).sort(newestFirst);
  }, [data]);
  const allTests = groupTests(attempts);
  const unassigned = groupTests(attempts.filter((attempt) => attempt.has_assignment === false));
  const date = (value: string | null) => value
    ? dayjs.utc(value).tz(getTimezoneByRegion(data?.region)).format('DD.MM.YYYY HH:mm') : '—';

  return (
    <section className="ab-completion-history" style={{ marginTop: 20 }} aria-label="История прохождений">
      <Title level={5}>История прохождений</Title>
      {(!state || state.userId !== userId || state.status === 'loading') && <Skeleton active paragraph={{ rows: 1 }} />}
      {state?.userId === userId && state.status === 'error' && (
        <Alert type="warning" showIcon message={state.error}
          action={<Button size="small" onClick={() => setRetry((value) => value + 1)}>Повторить</Button>} />
      )}
      {state?.userId === userId && state.status === 'ready' && (
        <>
          <Space size={6} wrap style={{ marginBottom: 12 }}>
            <Tag>Тестов в статистике: {allTests.length}</Tag>
            <Tag color="green">Сдано: {allTests.filter((test) => test.latest.is_passed === true).length}</Tag>
            <Tag>Завершённых попыток: {attempts.length}</Tag>
            {!!attempts.length && <Button size="small" onClick={() => setOpen(true)}>
              Все прохождения ({attempts.length})
            </Button>}
          </Space>
          {unassigned.length > 0 && (
            <>
              <Paragraph type="secondary">
                Без назначения: {unassigned.length}. Эти тесты пройдены ранее; результаты сохранены в статистике.
              </Paragraph>
              <Table<HistoricalTest> className="ab-legacy-results" size="small" rowKey="test_id"
                dataSource={unassigned} pagination={false} scroll={{ x: 580 }} columns={[
                  { title: 'Пройденный тест', key: 'historical-test', render: (_, row) => (
                    <Space direction="vertical" size={0}>
                      <Text>{row.latest.test_title || `Тест #${row.test_id}`}</Text>
                      <Text type="secondary">#{row.test_id} · без назначения</Text>
                    </Space>
                  ) },
                  { title: 'Завершён', key: 'historical-date', width: 145, render: (_, row) => date(row.latest.completed_at) },
                  { title: 'Последний результат', key: 'historical-result', width: 160, render: (_, row) => result(row.latest) },
                  { title: 'Попыток', key: 'historical-count', width: 80, render: (_, row) => row.attempts },
                ]} />
            </>
          )}
          {!attempts.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Завершённых попыток нет" />}
          <Modal open={open} title="Все завершённые попытки" onCancel={() => setOpen(false)} footer={null} width={900}>
            <Paragraph type="secondary">
              В статистике учитывается последний результат каждого теста. Даты завершения показаны по времени региона сотрудника.
            </Paragraph>
            <Table<CompletedAttempt> size="small" rowKey="attempt_id" dataSource={attempts}
              pagination={{ pageSize: 10 }} scroll={{ x: 700 }} columns={[
                { title: 'Пройденный тест', key: 'completed-test', render: (_, row) => (
                  <Space direction="vertical" size={0}><Text>{row.test_title}</Text><Text type="secondary">#{row.test_id}</Text></Space>
                ) },
                { title: 'Завершён', key: 'completed-date', width: 145, render: (_, row) => date(row.completed_at) },
                { title: 'Результат', key: 'completed-result', width: 150, render: (_, row) => result(row) },
                { title: 'Учёт', key: 'completed-accounting', width: 170, render: (_, row) => (
                  <Space direction="vertical" size={2}>
                    {row.has_assignment === false && <Tag>Без назначения</Tag>}
                    {row.is_counted === false ? <Tag>Заменена пересдачей</Tag>
                      : row.is_counted === true ? <Tag color="blue">В статистике</Tag> : null}
                  </Space>
                ) },
              ]} />
          </Modal>
        </>
      )}
    </section>
  );
};

export default EmployeeCompletionHistory;
